import * as React from "react";
import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { installDomMutationGuard } from "./lib/domMutationGuard";
import { initHubSpotNoCollect, loadHubSpotScript } from "./lib/hubspot";
import { capturePrivateAccessToken } from "./lib/privateAccessToken";
import { sanitizeSentryEventUrls } from "./lib/sentryPrivacy";
import { loadSiteWidget } from "./lib/siteWidget";

// This must run before Sentry, Replay, HubSpot, or the site widget can observe
// location.href. Pages/hooks read the captured value back from the same
// pathname-scoped session key.
capturePrivateAccessToken();
initHubSpotNoCollect();

const isProduction =
  window.location.hostname === "helpgenie.ai" ||
  window.location.hostname === "www.helpgenie.ai";
const isLocalhost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

if (!isLocalhost) {
  Sentry.init({
    dsn: isProduction
      ? "https://30700f0e4ca66da561a1d2f41fe73f38@o4510950461145088.ingest.us.sentry.io/4510950462259200"
      : "https://e552a432ad6de2a4a8a6e010d7bdc65b@o4510950461145088.ingest.us.sentry.io/4510950553616384",
    environment: isProduction ? "production" : "staging",
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        beforeAddRecordingEvent(event) {
          return sanitizeSentryEventUrls(event);
        },
      }),
    ],
    tracesSampleRate: isProduction ? 0.2 : 1.0,
    tracePropagationTargets: [/^https:\/\/helpgenie\.ai\/api/],
    replaysSessionSampleRate: isProduction ? 0.1 : 0.5,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    ignoreErrors: [
      /^uncaught exception: undefined$/,
      "The WKWebView was deallocated before the message was delivered",
      /window\.webkit\.messageHandlers/,
    ],
    beforeSend(event, hint) {
      const err = hint?.originalException;
      if (
        err &&
        typeof err === "object" &&
        "name" in err &&
        err.name === "AbortError"
      ) {
        return null;
      }
      const frames =
        event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ??
        [];
      if (frames.some((f) => f.function?.startsWith("tmsa"))) {
        return null;
      }
      return sanitizeSentryEventUrls(event);
    },
    beforeSendTransaction(event) {
      return sanitizeSentryEventUrls(event);
    },
    beforeBreadcrumb(breadcrumb) {
      return sanitizeSentryEventUrls(breadcrumb);
    },
  });
}

// Both scripts were previously injected inline by index.html, which ran before
// this module and could expose the unsanitized address in request referrers.
loadHubSpotScript();
loadSiteWidget();

installDomMutationGuard();

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

const root = createRoot(rootElement);
root.render(<App />);
